// PixiJS world renderer — 60Hz, reads state, never mutates it. Depth-sorts by
// tile position so the isometric stacking stays correct.

import {
  Application,
  ColorMatrixFilter,
  Container,
  Graphics,
  Rectangle,
  Sprite,
} from 'pixi.js';
import type { Breed, ChickenEntity, ChickenState, GameState } from '../core/types';
import { assets } from './assetLoader';
import {
  chickenFramePath,
  clipFramesFor,
  chickenReferencePath,
  cosmeticPath,
  STRUCTURE_HEIGHT_UNITS,
  STRUCTURE_SPRITES,
  TILE_SPRITES,
} from './spriteManifest';
import { metaFor } from './spriteMeta';
import { shade } from './palette';
import { TILE_DEPTH, TILE_WIDTH } from './iso';
import { AnimationStepper } from './animationStepper';
import { Camera } from './camera';
import { TapInput } from './tapInput';
import { actorDepth, footprintShadow, shadowDepth } from './depth';
import { shadowScale, shadowTexture } from './shadowTexture';
import {
  depthSort,
  tileToWorld,
  TILE_HEIGHT,
  worldToTileSnapped,
  type Point,
} from './iso';
import { CLAY } from './palette';
import {
  ChickenPlaceholder,
  drawTile,
  drawTileHighlight,
} from './proceduralSprites';
import { drawStructure } from './structureShapes';
import { effectsOf, getItem } from '../sim/items';
import { layoutStructures, structureAtTile } from '../sim/structureLayout';
import { tintFor } from './dayNight';
import { WeatherFx } from './weatherFx';

const STAGE_SCALE = { egg: 0.45, chick: 0.6, pullet: 0.82, adult: 1 } as const;

/**
 * Contact-shadow opacity. Low on purpose: the shadow's job is to say "this
 * touches the ground", and a stronger one competes with the birds for
 * attention, which is the opposite of the grounding it was added for.
 */
const STRUCTURE_SHADOW_ALPHA = 0.26;

const warnedAssets = new Set<string>();

/**
 * Half of the 44 CSS px accessibility floor (UI-DESIGN §6). A pullet's sprite
 * is a fraction of that on screen, so hit-testing the sprite's own bounds
 * would fail exactly the taps it most needs to accept — a child's imprecise
 * first attempts. Screen-space and zoom-independent on purpose: the target
 * has to stay tappable whether the camera is zoomed in or all the way out.
 */
const CHICKEN_HIT_RADIUS_PX = 26;
/** Adult on-screen height at zoom 1, matching ChickenPlaceholder's targetHeight. */
const BIRD_DRAWN_HEIGHT_PX = 92;

/** Breathing room left around the scene, in CSS px, on every side. The HUD
 *  overlays the top and bottom edges, so the margin is not optional. */
const SCENE_FIT_MARGIN_PX = 24;

/**
 * A tile with no pipeline metadata has to be placed by guessing from its image
 * bounds, which only tessellates if the render happens to be an exact 2:1
 * diamond with no padding. Say so once rather than silently looking broken.
 */
function warnUnmeasuredTile(path: string, width: number, height: number): void {
  if (warnedAssets.has(path)) return;
  warnedAssets.add(path);
  console.warn(
    `[assets] ${path} (${width}×${height}) has no entry in sprites.json; placing it from ` +
      'image bounds. Run `npm run assets:refit` so it gets a measured pivot.',
  );
}

/**
 * Stable per-tile pseudo-random value in [0, 1). Keyed off the coordinates so a
 * tile looks the same every frame, across a reload, and after a save round
 * trip — anything derived per-frame would crawl.
 */
function tileNoise(x: number, y: number, salt: number): number {
  let h = (x * 374761393 + y * 668265263 + salt * 2246822519) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
  return (h % 65536) / 65536;
}

/** Stable per-bird sub-tile offset, derived from its id so it never jitters. */
function birdJitter(id: string): { x: number; y: number } {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return {
    x: (((hash & 0xff) / 255) - 0.5) * 0.45,
    y: ((((hash >> 8) & 0xff) / 255) - 0.5) * 0.45,
  };
}

/** Writes a colour-matrix equivalent of multiplying by a tint colour, in place. */
function writeChannelScale(target: ColorMatrixFilter['matrix'], color: number): void {
  const r = ((color >> 16) & 0xff) / 255;
  const g = ((color >> 8) & 0xff) / 255;
  const b = (color & 0xff) / 255;
  target[0] = r;
  target[5] = g;
  target[10] = b;
}

export interface WorldRendererOptions {
  onTileTap?: (tile: Point, screen: Point) => void;
  onChickenTap?: (chickenId: string, screen: Point) => void;
  onStructureTap?: (structureId: string, screen: Point) => void;
}

export class WorldRenderer {
  readonly app: Application;
  readonly camera = new Camera();
  readonly animation = new AnimationStepper();

  private world = new Container();
  private tileLayer = new Container();
  /**
   * Birds and structures together, because they have to be able to sort against
   * each other. `sortableChildren` only ever orders a container's own children,
   * so while these were two sibling layers every bird was painted after every
   * structure no matter what its zIndex said — a hen walked behind the coop
   * still drew over it.
   */
  private actorLayer = new Container();
  private highlight = new Graphics();
  /** Screen-space, above the world: night darkening then weather particles. */
  private nightOverlay = new Graphics();
  private weatherFx = new WeatherFx();
  private colorMatrix = new ColorMatrixFilter();
  private tintMatrix: ColorMatrixFilter['matrix'] = [
    1, 0, 0, 0, 0,
    0, 1, 0, 0, 0,
    0, 0, 1, 0, 0,
    0, 0, 0, 1, 0,
  ];
  private tintArea = new Rectangle(0, 0, 1, 1);
  /** Whether the day/night filter is currently attached to the world. */
  private tinted = false;
  private structureGraphics = new Map<string, Graphics>();
  private structureSprites = new Map<string, Sprite>();
  private structureShadows = new Map<string, Sprite>();
  private tileGraphics = new Map<string, Graphics>();
  private tileSprites = new Map<string, Sprite>();
  /** Last drawn appearance per tile, so only changed tiles get repainted. */
  private tileLook = new Map<string, string>();
  private birds = new Map<string, ChickenPlaceholder>();
  private controls: TapInput | null = null;
  private state: Readonly<GameState> | null = null;
  /** Bird currently under the pointer, if any — drives cursor + outline. */
  private hoveredChickenId: string | null = null;
  private lastFrameMs = 0;
  private frameHandlers = new Set<(realDeltaMs: number) => void>();

  constructor(
    private host: HTMLElement,
    private options: WorldRendererOptions = {},
  ) {
    this.app = new Application({
      background: 0x1d1a17,
      antialias: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
      resizeTo: host,
    });
    this.world.addChild(this.tileLayer, this.highlight, this.actorLayer);
    this.app.stage.addChild(this.world, this.nightOverlay, this.weatherFx);
    // Painter's algorithm: everything in world space sorts by depth key. Tiles
    // are included because real tile art overhangs its own diamond, so grid
    // order alone puts far tiles on top of near ones.
    this.tileLayer.sortableChildren = true;
    this.actorLayer.sortableChildren = true;
    this.highlight.visible = false;
  }

  clearHighlight(): void {
    this.highlight.visible = false;
  }

  async mount(): Promise<void> {
    const canvas = this.app.view as unknown as HTMLCanvasElement;
    canvas.style.touchAction = 'none';
    this.host.appendChild(canvas);
    this.camera.resize(this.host.clientWidth, this.host.clientHeight);
    this.weatherFx.resize(this.host.clientWidth, this.host.clientHeight);

    this.controls = new TapInput(canvas, {
      onTap: (screen) => this.handleTap(screen),
      onContextTap: (screen) => this.handleContextTap(screen),
    });

    // Hover is its own listener rather than folded into TapInput: a player has
    // to see a bird is tappable before they ever press, and that has to work on
    // every pointer move, not just the ones that end in a tap.
    canvas.addEventListener('pointermove', this.handlePointerMove);
    canvas.addEventListener('pointerleave', this.clearHover);
    canvas.addEventListener('pointercancel', this.clearHover);

    window.addEventListener('resize', this.handleResize);

    this.lastFrameMs = performance.now();
    // Framing needs the real viewport, which only exists after the resize
    // above — boot may have called fitScene() while mounting was still
    // pending, so redo it now that the size is known.
    this.fitScene();
    this.app.ticker.add(this.onFrame);
  }

  /** Register per-frame work (drives the tick engine from the render loop). */
  onEachFrame(handler: (realDeltaMs: number) => void): () => void {
    this.frameHandlers.add(handler);
    return () => this.frameHandlers.delete(handler);
  }

  setState(state: Readonly<GameState>): void {
    this.state = state;
    // A wholesale swap invalidates every cached tile appearance.
    this.tileLook.clear();
    this.syncTiles();
  }

  /**
   * Frames the whole coop scene for the host and never lets the player move it.
   *
   * REFOCUS-2026-09.md §4: "One composed coop diorama, no pan or zoom." The
   * camera centres on the coop — the anchor the yard is composed around — and
   * picks the zoom that just contains the structures and the birds inside a
   * 24px margin, so the same farm is composed the same way on a phone and on a
   * desktop. Called on mount and on every resize; a player gesture cannot
   * change it.
   */
  fitScene(): void {
    if (!this.state) return;
    const viewW = this.camera.viewWidth;
    const viewH = this.camera.viewHeight;
    if (viewW <= 0 || viewH <= 0) return;

    const layout = layoutStructures(this.state);
    // World-space bounds of everything worth looking at: the yard's tiles, the
    // equipment on them and the birds walking between. An empty or nearly-empty
    // yard still needs a sane frame, so the grid itself is always in the set.
    const min = { x: Infinity, y: Infinity };
    const max = { x: -Infinity, y: -Infinity };
    const include = (wx: number, wy: number): void => {
      if (wx < min.x) min.x = wx;
      if (wy < min.y) min.y = wy;
      if (wx > max.x) max.x = wx;
      if (wy > max.y) max.y = wy;
    };

    const { width, height } = this.state.grid;
    for (const structure of this.state.structures) {
      const spot = layout.get(structure.id);
      if (!spot) continue;
      const w = tileToWorld(spot.x, spot.y);
      const [fw, fh] = getItem(structure.itemId)?.footprint ?? [1, 1];
      // Footprints grow right/down in iso, so the box is the anchor tile plus
      // the footprint's own half-extents.
      include(w.x - TILE_WIDTH / 2, w.y - TILE_DEPTH / 2);
      include(
        w.x + ((fw - 1) * TILE_WIDTH) / 2 + TILE_WIDTH / 2,
        w.y + ((fh - 1) * TILE_DEPTH) / 2 + TILE_HEIGHT / 2,
      );
    }
    for (const chicken of this.state.flock) {
      if (!chicken.alive) continue;
      const w = tileToWorld(chicken.position.x, chicken.position.y);
      include(w.x, w.y);
      // Birds stand well above their tile; a hen at the top of the frame would
      // otherwise be clipped by the HUD.
      include(w.x, w.y - BIRD_DRAWN_HEIGHT_PX * STAGE_SCALE[chicken.stage]);
    }
    // The yard floor, so a fresh farm with almost nothing in it still reads as a
    // yard rather than a dot.
    const corner = tileToWorld(width / 2, height / 2);
    include(corner.x - ((width + height) * TILE_WIDTH) / 2, corner.y - (width + height) * (TILE_HEIGHT / 2));
    include(corner.x + ((width + height) * TILE_WIDTH) / 2, corner.y + (width + height) * (TILE_HEIGHT / 2));

    if (!Number.isFinite(min.x) || !Number.isFinite(min.y)) return;

    // Centre on the coop when there is one: it is the anchor the scene is
    // composed around. Otherwise fall back to the middle of the framed bounds.
    const coop = this.coopAnchor(layout);
    const centre = coop ?? { x: (min.x + max.x) / 2, y: (min.y + max.y) / 2 };
    this.camera.centerOn(centre.x, centre.y);

    const spanX = Math.max(1, max.x - min.x);
    const spanY = Math.max(1, max.y - min.y);
    const fit = Math.min(
      (viewW - SCENE_FIT_MARGIN_PX * 2) / spanX,
      (viewH - SCENE_FIT_MARGIN_PX * 2) / spanY,
    );
    this.camera.setZoom(fit);
  }

  /** World position of the yard's coop, or undefined when none is owned yet. */
  private coopAnchor(layout: Map<string, Point>): Point | undefined {
    const structure = this.state?.structures.find((s) => effectsOf(s).coop);
    if (!structure) return undefined;
    const spot = layout.get(structure.id) ?? { x: 0, y: 0 };
    const w = tileToWorld(spot.x, spot.y);
    return { x: w.x, y: w.y - TILE_HEIGHT / 2 };
  }

  private handleResize = (): void => {
    this.camera.resize(this.host.clientWidth, this.host.clientHeight);
    this.weatherFx.resize(this.host.clientWidth, this.host.clientHeight);
    // The scene is composed for the host, so a rotate or a resize has to refit.
    this.fitScene();
  };

  /** Repaints the night overlay and weather particles from current state. */
  private syncAtmosphere(realDeltaMs: number): void {
    if (!this.state) return;
    const tint = tintFor(this.state.simHour, this.state.weather);

    // Containers have no tint in Pixi v7, so the time-of-day colour is applied
    // as a channel-scaling colour matrix over the whole world. A filter forces
    // the world into an offscreen buffer and breaks sprite batching, so it is
    // only attached when it would actually change something — which excludes
    // clear daylight, the most common state in the game.
    const wantsTint = tint.color !== 0xffffff;
    if (wantsTint !== this.tinted) {
      this.tinted = wantsTint;
      this.world.filters = wantsTint ? [this.colorMatrix] : null;
    }
    if (wantsTint) {
      // Reused instances: allocating a fresh 20-element matrix and Rectangle
      // every frame (all dusk/night/dawn, i.e. most of a Real-Time day) made
      // the GC churn for nothing.
      writeChannelScale(this.tintMatrix, tint.color);
      this.colorMatrix.matrix = this.tintMatrix;
      this.tintArea.x = 0;
      this.tintArea.y = 0;
      this.tintArea.width = this.camera.viewWidth;
      this.tintArea.height = this.camera.viewHeight;
      this.world.filterArea = this.tintArea;
    }
    this.nightOverlay.clear();
    if (tint.darkness > 0) {
      this.nightOverlay.beginFill(0x0a1020, tint.darkness);
      this.nightOverlay.drawRect(0, 0, this.camera.viewWidth, this.camera.viewHeight);
      this.nightOverlay.endFill();
    }

    this.weatherFx.setCondition(this.state.weather.condition);
    this.weatherFx.setReducedMotion(this.state.settings.reducedMotion);
    this.weatherFx.update(realDeltaMs);
  }

  private onFrame = (): void => {
    const now = performance.now();
    const realDeltaMs = Math.min(now - this.lastFrameMs, 250);
    this.lastFrameMs = now;

    for (const handler of [...this.frameHandlers]) {
      try {
        handler(realDeltaMs);
      } catch (err) {
        console.error('[WorldRenderer] frame handler threw', err);
      }
    }

    // Sprite frames advance on their own 12fps clock, not this one.
    this.animation.advance(realDeltaMs);

    const t = this.camera.getTransform();
    this.world.position.set(t.x, t.y);
    this.world.scale.set(t.scale);

    this.syncTiles();
    this.syncStructures();
    this.syncEntities();
    this.syncAtmosphere(realDeltaMs);
  };

  /** Owned equipment: real sprite when one exists, shaped vector clay otherwise. */
  private syncStructures(): void {
    if (!this.state) return;
    const seen = new Set<string>();
    const layout = layoutStructures(this.state);

    for (const structure of this.state.structures) {
      seen.add(structure.id);
      const item = getItem(structure.itemId);
      const anchor = layout.get(structure.id);
      if (!anchor) continue;
      const [footW, footH] = item?.footprint ?? [1, 1];
      // Both paths stand the object at the middle of its footprint, so a 3×2
      // coop straddles its six tiles instead of hanging off the origin one.
      const centre = tileToWorld(anchor.x + footW / 2, anchor.y + footH / 2);
      const path = STRUCTURE_SPRITES[structure.itemId];
      const texture = path ? assets.getOrRequest(path) : null;
      // Sorting on the centre rather than the origin tile keeps a bird walking
      // along the far side of a wide coop behind it.
      const depth = actorDepth(anchor.x + footW / 2, anchor.y + footH / 2);

      if (texture) {
        const g = this.structureGraphics.get(structure.id);
        if (g) {
          g.destroy();
          this.structureGraphics.delete(structure.id);
        }
        let sprite = this.structureSprites.get(structure.id);
        if (!sprite) {
          sprite = new Sprite();
          sprite.roundPixels = true;
          this.structureSprites.set(structure.id, sprite);
          this.actorLayer.addChild(sprite);
        }
        if (sprite.texture !== texture) {
          sprite.texture = texture;
          const meta = path ? metaFor(path) : null;
          if (meta) {
            // The measured pivot is where the object meets the ground, so the
            // sprite sits on its tile regardless of how much headroom the
            // render included above it.
            sprite.anchor.set(meta.pivotX / meta.w, meta.pivotY / meta.h);
          } else {
            sprite.anchor.set(0.5, 1);
          }
          // Footprint width drives the size: a 2x2 coop covers twice the ground
          // a 1x1 feeder does, and must be drawn that way. Where the render was
          // not framed to true scale, an authored height is the tighter of the
          // two limits and keeps the object from towering over the flock.
          const widthFit = (TILE_WIDTH * footW) / (meta?.diamondW ?? texture.width);
          const units = STRUCTURE_HEIGHT_UNITS[structure.itemId];
          const heightFit = units ? (units * TILE_DEPTH) / texture.height : Infinity;
          sprite.scale.set(Math.min(widthFit, heightFit));
        }
        sprite.position.set(centre.x, centre.y);
        sprite.zIndex = depth;
        this.syncStructureShadow(structure.id, centre, depth, footW, footH);
        continue;
      }

      this.dropStructureShadow(structure.id);

      let g = this.structureGraphics.get(structure.id);
      if (!g) {
        g = new Graphics();
        this.structureGraphics.set(structure.id, g);
        this.actorLayer.addChild(g);
      }
      g.position.set(centre.x, centre.y);
      g.zIndex = depth;
      drawStructure(g, structure.itemId, footW, footH);
    }

    for (const [id, g] of this.structureGraphics) {
      if (!seen.has(id)) {
        g.destroy();
        this.structureGraphics.delete(id);
      }
    }
    for (const [id, sprite] of this.structureSprites) {
      if (!seen.has(id)) {
        sprite.destroy();
        this.structureSprites.delete(id);
      }
    }
    for (const id of this.structureShadows.keys()) {
      if (!seen.has(id)) this.dropStructureShadow(id);
    }
  }

  /**
   * Contact shadow for a real-art structure. Built once and then only
   * repositioned, because its geometry comes from the footprint alone and the
   * footprint does not change while the structure stands.
   *
   * The procedural fallback is deliberately excluded: those shapes already
   * paint their own ground patch (structureShapes' `ground()`), and stacking
   * both makes the object sit in a dark pool.
   */
  private syncStructureShadow(
    id: string,
    centre: Point,
    depth: number,
    footW: number,
    footH: number,
  ): void {
    let shadow = this.structureShadows.get(id);
    if (!shadow) {
      const { rx, ry } = footprintShadow(footW, footH);
      const fit = shadowScale(rx, ry);
      shadow = new Sprite(shadowTexture());
      shadow.anchor.set(0.5);
      shadow.tint = CLAY.shadow;
      shadow.alpha = STRUCTURE_SHADOW_ALPHA;
      shadow.scale.set(fit.x, fit.y);
      this.structureShadows.set(id, shadow);
      this.actorLayer.addChild(shadow);
    }
    shadow.position.set(centre.x, centre.y);
    shadow.zIndex = shadowDepth(depth);
  }

  private dropStructureShadow(id: string): void {
    const shadow = this.structureShadows.get(id);
    if (!shadow) return;
    shadow.destroy();
    this.structureShadows.delete(id);
  }

  /** Adds/removes tile visuals and repaints only the ones that changed. */
  private syncTiles(): void {
    if (!this.state) return;
    const seen = new Set<string>();

    for (const tile of this.state.grid.tiles) {
      const key = `${tile.x},${tile.y}`;
      seen.add(key);
      const pos = tileToWorld(tile.x, tile.y);
      const texture = assets.getOrRequest(TILE_SPRITES[tile.terrainType]);

      if (texture) {
        // Real art available: drop the primitive for this tile if it has one.
        const g = this.tileGraphics.get(key);
        if (g) {
          g.destroy();
          this.tileGraphics.delete(key);
        }
        let sprite = this.tileSprites.get(key);
        if (!sprite) {
          sprite = new Sprite();
          // Whole pixels only: at 128px a tile lands on fractional device
          // pixels constantly while panning, which shimmers along every seam.
          sprite.roundPixels = true;
          this.tileSprites.set(key, sprite);
          this.tileLayer.addChild(sprite);
        }
        if (sprite.texture !== texture) {
          sprite.texture = texture;
          const path = TILE_SPRITES[tile.terrainType];
          const meta = metaFor(path);
          if (meta?.diamondW) {
            // Scale by the diamond, not the image: grass blades overhang the
            // top face, and sizing to the bounding box would shrink the tile
            // they belong to. The pivot is the diamond's apex corner, which is
            // exactly what tileToWorld returns.
            sprite.anchor.set(meta.pivotX / meta.w, meta.pivotY / meta.h);
            sprite.scale.set(TILE_WIDTH / meta.diamondW);
          } else {
            sprite.anchor.set(0.5, 0);
            sprite.scale.set(TILE_WIDTH / texture.width);
            warnUnmeasuredTile(path, texture.width, texture.height);
          }
          // One grass render across the whole yard reads as wallpaper. Mirroring
          // costs nothing and is safe: the diamond is symmetric about the apex
          // the sprite is anchored to, so a flipped tile covers the same ground.
          if (tileNoise(tile.x, tile.y, 1) < 0.5) sprite.scale.x *= -1;
        }
        sprite.position.set(pos.x, pos.y);
        // Overhang means neighbours overlap, so tiles need the same painter's
        // order as everything else — nearer tiles draw over farther ones.
        sprite.zIndex = depthSort(tile.x, tile.y);
        // Soiled litter darkens the tile rather than needing a second asset.
        // A little per-tile darkening on top breaks up the remaining repetition;
        // tint can only multiply down, so this shades rather than brightens.
        sprite.tint = shade(
          0xffffff,
          (tile.dirtinessLevel / 100) * 0.35 + tileNoise(tile.x, tile.y, 2) * 0.08,
        );
        continue;
      }

      let g = this.tileGraphics.get(key);
      if (!g) {
        g = new Graphics();
        this.tileGraphics.set(key, g);
        this.tileLayer.addChild(g);
        g.position.set(pos.x, pos.y);
        g.zIndex = depthSort(tile.x, tile.y);
      }
      // Dirtiness shifts the fill, so it is part of the appearance key.
      const look = `${tile.terrainType}:${Math.round(tile.dirtinessLevel)}`;
      if (this.tileLook.get(key) !== look) {
        this.tileLook.set(key, look);
        drawTile(g, tile.terrainType, tile.dirtinessLevel);
      }
    }

    for (const [key, g] of this.tileGraphics) {
      if (!seen.has(key)) {
        g.destroy();
        this.tileGraphics.delete(key);
        this.tileLook.delete(key);
      }
    }
    for (const [key, sprite] of this.tileSprites) {
      if (!seen.has(key)) {
        sprite.destroy();
        this.tileSprites.delete(key);
      }
    }
  }

  private syncEntities(): void {
    if (!this.state) return;
    const seen = new Set<string>();

    for (const chicken of this.state.flock) {
      seen.add(chicken.id);
      let sprite = this.birds.get(chicken.id);
      if (!sprite) {
        sprite = new ChickenPlaceholder({
          breed: chicken.breed,
          scale: STAGE_SCALE[chicken.stage],
        });
        this.birds.set(chicken.id, sprite);
        this.actorLayer.addChild(sprite);
      }
      sprite.setBreed(chicken.breed);
      // Escaped birds are outside the run; hide rather than draw them adrift.
      sprite.visible = chicken.currentState !== 'ESCAPED';

      // Tile centre = +0.5 on both axes, plus a small per-bird offset so birds
      // sharing a tile stay individually visible instead of stacking exactly.
      const jitter = birdJitter(chicken.id);
      const drawX = chicken.position.x + 0.5 + jitter.x;
      const drawY = chicken.position.y + 0.5 + jitter.y;
      const p = tileToWorld(drawX, drawY, chicken.position.tileZ);
      sprite.position.set(p.x, p.y + TILE_HEIGHT / 2);
      // Keyed on where the bird is drawn, not on its integer tile. The old key
      // put every bird half a tile further back than it actually stood, which
      // is enough for a hen to sort behind a feeder she is plainly in front of.
      sprite.zIndex = actorDepth(drawX, drawY, chicken.position.tileZ);

      // Prefer the animated frame; fall back to the canonical reference pose;
      // fall back again to the procedural bird. Whichever exists today.
      const isChick = chicken.stage === 'chick' || chicken.stage === 'egg';
      // Breed-aware counts: a breed with no approved frames for this clip
      // (e.g. rejected chick idle frames) yields a null framePath, and no
      // clip file is requested at all — straight to the reference pose.
      const frameCount = clipFramesFor(chicken.currentState, isChick, chicken.breed);
      const frame = this.animation.frameIn(frameCount);
      const framePath = chickenFramePath(chicken.breed, chicken.currentState, frame, 'fr', isChick);
      const referencePath = chickenReferencePath(chicken.breed, isChick, chicken.gender);
      // Fall back per CLIP, not per frame. A clip animates only when every
      // frame its own count asks for is present; otherwise the whole loop holds
      // the reference pose. Deciding per frame means art that is merely short
      // of its count alternates between an animated frame and the still —
      // art, art, still, still — which reads as a strobe rather than as a
      // missing asset. Probing the clip also warms it, so the later frames
      // decode while this one plays, and getOrRequest caches misses.
      // Probe the whole clip, then pick from it. Probing also warms the cache,
      // so the later frames decode while this one plays; getOrRequest caches
      // misses, so this costs nothing after the first pass.
      let path: string | null = null;
      // A null framePath means the breed has no approved frames for this
      // clip: skip the probe entirely and fall back to the reference pose
      // without referencing any rejected frame file.
      const clipComplete =
        framePath !== null && clipIsWarm(chicken.breed, chicken.currentState, isChick);
      let texture = clipComplete && framePath ? assets.getOrRequest(framePath) : null;
      if (!texture) {
        texture = assets.getOrRequest(referencePath);
        if (texture) path = referencePath;
      } else {
        path = framePath;
      }
      sprite.setTexture(texture, path ? metaFor(path) : null);
      sprite.setFrame(this.animation.getFrame(), chicken.currentState);
      // R5 wardrobe: equipped cosmetic blits over the bird, same canvas.
      const trimPath = chicken.cosmeticId ? cosmeticPath(chicken.cosmeticId) : null;
      sprite.setCosmetic(trimPath ? assets.getOrRequest(trimPath) : null);
    }

    for (const [id, sprite] of this.birds) {
      if (!seen.has(id)) {
        sprite.destroy({ children: true });
        this.birds.delete(id);
        // Otherwise the next hover check tries to un-hover a sprite that no
        // longer exists, and the cursor stays stuck on 'pointer'.
        if (id === this.hoveredChickenId) this.hoveredChickenId = null;
      }
    }
  }

  /** Canvas-local (CSS px) point for a raw client-space event/tap point. */
  private toCanvasPoint(screen: Point): Point {
    const rect = (this.app.view as unknown as HTMLCanvasElement).getBoundingClientRect();
    return { x: screen.x - rect.left, y: screen.y - rect.top };
  }

  /**
   * Nearest live chicken within the generous tap radius, screen-space so it
   * holds at any zoom. Ties go to the closest bird rather than draw order.
   *
   * The sprite anchor is the bird's *feet*, but the bird a player aims at is the
   * body about two thirds of the way up. Measuring from the anchor made a chick
   * tappable only along a band at ground level — clicking the chick itself fell
   * through to the tile underneath. The centre is lifted to the body, and the
   * radius grows with zoom so the target tracks the drawn bird instead of
   * shrinking to a dot when zoomed out.
   */
  private findChickenAt(canvasPoint: Point): ChickenEntity | undefined {
    if (!this.state) return undefined;
    let best: ChickenEntity | undefined;
    let bestDist = Infinity;
    for (const chicken of this.state.flock) {
      if (chicken.currentState === 'ESCAPED') continue;
      const jitter = birdJitter(chicken.id);
      const p = tileToWorld(
        chicken.position.x + 0.5 + jitter.x,
        chicken.position.y + 0.5 + jitter.y,
        chicken.position.tileZ,
      );
      const feet = this.camera.worldToScreen(p.x, p.y + TILE_HEIGHT / 2);
      const drawnHeight = BIRD_DRAWN_HEIGHT_PX * STAGE_SCALE[chicken.stage] * this.camera.zoom;
      const centre = { x: feet.x, y: feet.y - drawnHeight * 0.5 };
      // Never below the 44px minimum touch target, however far out the camera is.
      const radius = Math.max(CHICKEN_HIT_RADIUS_PX, drawnHeight * 0.5);
      const dist = Math.hypot(centre.x - canvasPoint.x, centre.y - canvasPoint.y);
      if (dist <= radius && dist <= bestDist) {
        bestDist = dist;
        best = chicken;
      }
    }
    return best;
  }

  private handleTap(screen: Point): void {
    if (!this.state) return;
    const canvasPoint = this.toCanvasPoint(screen);

    // A bird wins over whatever tile or structure is beneath it — a chick is
    // too small and too important to lose to the ground it's standing on.
    const chicken = this.findChickenAt(canvasPoint);
    if (chicken) {
      this.options.onChickenTap?.(chicken.id, screen);
      return;
    }

    const world = this.camera.screenToWorld(canvasPoint.x, canvasPoint.y);
    const tile = worldToTileSnapped(world.x, world.y);
    const { width, height } = this.state.grid;
    if (tile.x < 0 || tile.y < 0 || tile.x >= width || tile.y >= height) {
      this.highlight.visible = false;
      return;
    }
    const pos = tileToWorld(tile.x, tile.y);
    this.highlight.position.set(pos.x, pos.y);
    drawTileHighlight(this.highlight, CLAY.cream);
    this.highlight.visible = true;

    // A structure covering the tile gets the tap; the bare tile is the fallback.
    const structure = structureAtTile(this.state, layoutStructures(this.state), tile.x, tile.y);
    if (structure && this.options.onStructureTap) {
      this.options.onStructureTap(structure.id, screen);
      return;
    }
    this.options.onTileTap?.(tile, screen);
  }

  /**
   * Right-click: the same radial menu a tap opens, on a bird or a structure.
   * Empty ground does nothing — a context menu with nothing in it is worse
   * than none.
   */
  private handleContextTap(screen: Point): void {
    if (!this.state) return;
    const canvasPoint = this.toCanvasPoint(screen);

    const chicken = this.findChickenAt(canvasPoint);
    if (chicken) {
      this.options.onChickenTap?.(chicken.id, screen);
      return;
    }

    const world = this.camera.screenToWorld(canvasPoint.x, canvasPoint.y);
    const tile = worldToTileSnapped(world.x, world.y);
    const structure = structureAtTile(this.state, layoutStructures(this.state), tile.x, tile.y);
    if (structure && this.options.onStructureTap) {
      this.options.onStructureTap(structure.id, screen);
    }
  }

  private handlePointerMove = (e: PointerEvent): void => {
    if (!this.state) return;
    const canvasPoint = this.toCanvasPoint({ x: e.clientX, y: e.clientY });
    const hit = this.findChickenAt(canvasPoint);
    this.setHoveredChicken(hit?.id ?? null);
  };

  private clearHover = (): void => this.setHoveredChicken(null);

  private setHoveredChicken(id: string | null): void {
    if (id === this.hoveredChickenId) return;
    if (this.hoveredChickenId) this.birds.get(this.hoveredChickenId)?.setHovered(false);
    this.hoveredChickenId = id;
    if (id) this.birds.get(id)?.setHovered(true);
    // The cursor change is the affordance that says "this is tappable" before
    // the player commits to a press — the outline alone is easy to miss.
    (this.app.view as unknown as HTMLCanvasElement).style.cursor = id ? 'pointer' : '';
  }

  destroy(): void {
    const canvas = this.app.view as unknown as HTMLCanvasElement;
    canvas.removeEventListener('pointermove', this.handlePointerMove);
    canvas.removeEventListener('pointerleave', this.clearHover);
    canvas.removeEventListener('pointercancel', this.clearHover);
    window.removeEventListener('resize', this.handleResize);
    this.controls?.destroy();
    this.app.destroy(true, { children: true });
  }
}
/**
 * True when every frame this clip's own count asks for is already in the asset
 * cache, requesting any that are not so the whole clip is warm.
 *
 * The renderer needs this to decide a clip all at once. Per-frame fallback
 * means a clip that is merely short of its count alternates between an animated
 * frame and the reference pose — art, art, still, still — which reads as a
 * strobe rather than as a missing asset. The art is meant to be exactly as
 * short as ART-LAW §8 says, so "short" is the normal case, not the exception.
 */
function clipIsWarm(breed: Breed, state: ChickenState, isChick: boolean): boolean {
  const count = clipFramesFor(state, isChick, breed);
  if (count === 0) return false;
  let complete = true;
  for (let i = 0; i < count; i++) {
    const p = chickenFramePath(breed, state, i, 'fr', isChick);
    if (!p || !assets.getOrRequest(p)) complete = false;
  }
  return complete;
}

